(() => {
  'use strict';

  if (window.EdiBotVideo) return;

  const states = new Map();
  const warmups = new Map();
  const TIMEOUT = 12000;
  let warmedVideo;
  const bufferQueue = new Map();
  let bufferTask;
  let bufferSchedule;

  function clearTimers(state) {
    window.clearTimeout(state.timer);
    window.clearTimeout(state.retryTimer);
    state.timer = null;
    state.retryTimer = null;
  }

  function feedback(state, phase) {
    state.wrapper.dataset.edpVideoState = phase;
    const visible = ['loading', 'retrying', 'error', 'blocked'].includes(phase);
    if (state.feedback) state.feedback.hidden = !visible;
    if (state.message) {
      const key = `edpVideo${phase.charAt(0).toUpperCase()}${phase.slice(1)}`;
      state.message.textContent = visible ? state.wrapper.dataset[key] || '' : '';
      state.message.classList.toggle('edp-visually-hidden', phase === 'loading' || phase === 'retrying');
    }
    if (state.retry) state.retry.hidden = !['error', 'blocked'].includes(phase);
    if (state.playButton) state.playButton.hidden = !['idle', 'warmed', 'paused'].includes(phase);
    state.video.setAttribute('aria-busy', String(phase === 'loading' || phase === 'retrying'));
  }

  function isAvailable(state) {
    return state.video.isConnected && !document.hidden && !state.wrapper.closest('[hidden]');
  }

  function watchdog(state) {
    if (state.timer || !state.desired) return;
    const epoch = state.epoch;
    state.timer = window.setTimeout(() => {
      state.timer = null;
      if (state.epoch === epoch && state.desired) fail(state);
    }, TIMEOUT);
  }

  function stop(state, keepBuffer = false) {
    state.desired = false;
    state.epoch += 1;
    clearTimers(state);
    state.video.pause();
    if (!keepBuffer) state.video.preload = 'none';
  }

  function fail(state, error) {
    if (!state.desired) return;
    if (!isAvailable(state)) {
      pause(state.video);
      return;
    }
    if (state.retryTimer) return;
    if (error && error.name === 'NotAllowedError') {
      state.blocked = true;
      stop(state);
      feedback(state, 'blocked');
      return;
    }
    const position = Number.isFinite(state.video.currentTime) ? state.video.currentTime : 0;
    stop(state);
    if (state.retries >= 1) {
      state.failed = true;
      feedback(state, 'error');
      return;
    }
    state.retries += 1;
    state.desired = true;
    state.position = Math.max(state.position, position);
    feedback(state, 'retrying');
    const epoch = state.epoch;
    state.retryTimer = window.setTimeout(() => {
      state.retryTimer = null;
      if (state.epoch !== epoch || !state.desired || !isAvailable(state)) return;
      attempt(state, true);
    }, 300);
  }

  function attempt(state, reload = false) {
    state.epoch += 1;
    const epoch = state.epoch;
    clearTimers(state);
    state.desired = true;
    interruptBuffer(state.video);
    state.video.preload = 'auto';
    state.video.setAttribute('loading', 'eager');
    feedback(state, state.retries ? 'retrying' : 'loading');
    watchdog(state);
    try {
      if (reload) state.video.load();
      Promise.resolve(state.video.play()).then(() => {
        if (state.epoch !== epoch || !state.desired) return;
        if (!state.video.paused && state.video.readyState >= 3) {
          clearTimers(state);
          feedback(state, 'playing');
          watchdog(state);
        }
      }, error => {
        if (state.epoch === epoch && state.desired) fail(state, error);
      });
    } catch (error) {
      if (state.epoch === epoch) fail(state, error);
    }
  }

  function ensure(video) {
    if (states.has(video)) return states.get(video);
    const wrapper = video.closest('[data-edp-video]');
    if (!wrapper) return null;
    const state = {
      video, wrapper,
      feedback: wrapper.querySelector('[data-edp-video-feedback]'),
      message: wrapper.querySelector('[data-edp-video-message]'),
      retry: wrapper.querySelector('[data-edp-video-retry]'),
      playButton: wrapper.querySelector('[data-edp-video-play]'),
      desired: false, failed: false, blocked: false, userPaused: false,
      bufferAttempted: false,
      epoch: 0, retries: 0, position: 0, lastTime: 0,
      timer: null, retryTimer: null, listeners: [],
    };
    states.set(video, state);
    const on = (target, event, handler) => {
      if (!target) return;
      target.addEventListener(event, handler);
      state.listeners.push(() => target.removeEventListener(event, handler));
    };
    on(video, 'play', () => {
      if (video.paused) return;
      if (!isAvailable(state)) {
        pause(video);
        return;
      }
      if (state.desired) return;
      state.desired = true;
      interruptBuffer(video);
      state.failed = false;
      state.blocked = false;
      state.userPaused = false;
      state.retries = 0;
      state.epoch += 1;
      feedback(state, 'loading');
      watchdog(state);
    });
    on(video, 'playing', () => {
      if (!state.desired) return;
      clearTimers(state);
      feedback(state, 'playing');
      watchdog(state);
    });
    on(video, 'timeupdate', () => {
      if (!state.desired || video.currentTime === state.lastTime) return;
      state.lastTime = video.currentTime;
      if (video.paused) return;
      clearTimers(state);
      feedback(state, 'playing');
      watchdog(state);
    });
    ['waiting', 'stalled'].forEach(event => on(video, event, () => {
      if (!state.desired) return;
      feedback(state, state.retries ? 'retrying' : 'loading');
      watchdog(state);
    }));
    on(video, 'error', () => fail(state));
    on(video, 'loadedmetadata', () => {
      if (!state.desired || !state.position) return;
      try {
        const end = Number.isFinite(video.duration) ? Math.max(0, video.duration - 0.1) : state.position;
        video.currentTime = Math.min(state.position, end);
        state.position = 0;
      } catch (_) {}
    });
    on(video, 'pause', () => {
      if (!video.paused || !state.desired || state.retryTimer) return;
      state.userPaused = !video.ended;
      state.desired = false;
      state.epoch += 1;
      clearTimers(state);
      feedback(state, 'paused');
      scheduleBuffer();
    });
    on(video, 'ended', () => {
      state.desired = false;
      state.userPaused = true;
      clearTimers(state);
      feedback(state, 'paused');
      scheduleBuffer();
    });
    on(state.retry, 'click', () => play(video, { manual: true }));
    on(state.playButton, 'click', () => play(video, { manual: true }));
    feedback(state, 'idle');
    return state;
  }

  function play(video, { manual = false } = {}) {
    const state = ensure(video);
    if (!state || !isAvailable(state)) return false;
    if (!manual && (state.desired || state.failed || state.blocked || state.userPaused)) return false;
    if (manual && state.desired && !state.failed && !state.blocked) return true;
    const reload = state.failed || Boolean(video.error);
    state.failed = false;
    state.blocked = false;
    state.userPaused = false;
    state.retries = 0;
    state.position = 0;
    attempt(state, reload);
    return true;
  }

  function pause(video) {
    const state = states.get(video);
    if (!state) {
      video.pause();
      return;
    }
    const keepBuffer = !document.hidden && Boolean(state.bufferAnchor) && nearViewport(state.bufferAnchor);
    if (!keepBuffer && bufferTask?.video === video) interruptBuffer();
    stop(state, keepBuffer);
    if (!state.failed && !state.blocked) feedback(state, 'paused');
    scheduleBuffer();
  }

  function constrainedConnection() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return Boolean(connection && (connection.saveData || ['slow-2g', '2g'].includes(connection.effectiveType)));
  }

  function nearViewport(anchor) {
    if (!anchor?.isConnected || anchor.closest('[hidden]')) return false;
    const bounds = anchor.getBoundingClientRect();
    return bounds.bottom > -600 && bounds.top < window.innerHeight + 600;
  }

  function scheduleBuffer() {
    if (bufferSchedule !== undefined || bufferTask || !bufferQueue.size || document.hidden) return;
    const run = () => { bufferSchedule = undefined; drainBuffer(); };
    bufferSchedule = window.requestIdleCallback
      ? window.requestIdleCallback(run, { timeout: 1200 })
      : window.setTimeout(run, 250);
  }

  function interruptBuffer(requestedVideo) {
    if (bufferTask) bufferTask.finish('interrupted', requestedVideo);
  }

  function drainBuffer() {
    if (bufferTask || document.hidden || constrainedConnection()) return;
    if ([...states.values()].some(state => state.desired)) return;
    for (const [video, anchor] of bufferQueue) {
      bufferQueue.delete(video);
      const state = states.get(video);
      if (!state || state.bufferAttempted || state.failed || state.blocked || !video.isConnected || !nearViewport(anchor)) continue;
      state.bufferAttempted = true;
      state.bufferAnchor = anchor;
      let timer;
      const events = ['progress', 'canplay', 'canplaythrough', 'loadeddata'];
      const task = { video, finish };
      function finish(reason, requestedVideo) {
        if (bufferTask !== task) return;
        window.clearTimeout(timer);
        events.forEach(event => video.removeEventListener(event, check));
        video.removeEventListener('error', onError);
        bufferTask = undefined;
        state.wrapper.dataset.edpVideoBuffer = reason;
        if (reason === 'interrupted') {
          state.bufferAttempted = false;
          if (video !== requestedVideo) bufferQueue.set(video, anchor);
        }
        if (!state.desired && video !== requestedVideo) video.preload = 'metadata';
        scheduleBuffer();
      }
      function check() {
        if (video.readyState >= 4 || (video.buffered.length && video.buffered.end(0) >= Math.min(8, video.duration || 8))) finish('ready');
      }
      function onError() { finish('unavailable'); }
      bufferTask = task;
      events.forEach(event => video.addEventListener(event, check));
      video.addEventListener('error', onError);
      timer = window.setTimeout(() => finish('deferred'), TIMEOUT);
      state.wrapper.dataset.edpVideoBuffer = 'buffering';
      video.preload = 'auto';
      video.setAttribute('loading', 'eager');
      if (video.readyState === 0 && video.networkState !== 2) video.load();
      if (!state.userPaused) feedback(state, 'warmed');
      check();
      break;
    }
  }

  function warm(video, { anchor } = {}) {
    const state = ensure(video);
    if (!state || state.desired || state.bufferAttempted || state.failed || state.blocked || constrainedConnection() || document.hidden) return;
    anchor ||= state.wrapper;
    if (!nearViewport(anchor)) return;
    bufferQueue.delete(video);
    bufferQueue.set(video, anchor);
    scheduleBuffer();
  }

  function warmHero(group) {
    if (warmups.has(group)) return;
    if (constrainedConnection()) {
      group.dataset.edpVideoWarmup = 'connection-limited';
      return;
    }
    const video = group.querySelector('video');
    if (!video) return;
    if (warmedVideo && (warmedVideo !== video || video.readyState >= 2 || video.preload === 'auto')) return;
    const cover = group.querySelector('[data-edp-media-panel] img');
    let idleId;
    let timeoutId;
    const clean = () => {
      cover?.removeEventListener('load', schedule);
      cover?.removeEventListener('error', schedule);
      if (idleId !== undefined && window.cancelIdleCallback) window.cancelIdleCallback(idleId);
      window.clearTimeout(timeoutId);
      warmups.delete(group);
    };
    const beginHeroWarmup = () => {
      clean();
      if ((warmedVideo && warmedVideo !== video) || constrainedConnection() || document.hidden || !group.isConnected) {
        group.dataset.edpVideoWarmup = 'deferred';
        return;
      }
      const bounds = group.getBoundingClientRect();
      if (bounds.bottom <= 0 || bounds.top >= window.innerHeight) {
        group.dataset.edpVideoWarmup = 'offscreen';
        return;
      }
      const state = ensure(video);
      if (!state || state.desired || state.failed || state.blocked || state.userPaused) return;
      warmedVideo = video;
      group.dataset.edpVideoWarmup = 'buffering';
      warm(video, { anchor: group });
      drainBuffer();
    };
    const schedule = () => {
      group.dataset.edpVideoWarmup = 'scheduled';
      cover?.removeEventListener('load', schedule);
      cover?.removeEventListener('error', schedule);
      if (window.requestIdleCallback) idleId = window.requestIdleCallback(beginHeroWarmup, { timeout: 1800 });
      else timeoutId = window.setTimeout(beginHeroWarmup, 350);
    };
    warmups.set(group, clean);
    group.dataset.edpVideoWarmup = 'waiting-cover';
    if (!cover || cover.complete) schedule();
    else {
      cover.addEventListener('load', schedule, { once: true });
      cover.addEventListener('error', schedule, { once: true });
    }
  }

  function init(root = document) {
    root.querySelectorAll('[data-edp-video] video').forEach(ensure);
  }

  function destroy(root) {
    warmups.forEach((clean, group) => { if (root.contains(group)) clean(); });
    if (bufferTask && root.contains(bufferTask.video)) bufferTask.finish('removed');
    bufferQueue.forEach((anchor, video) => { if (root.contains(video)) bufferQueue.delete(video); });
    states.forEach((state, video) => {
      if (!root.contains(video)) return;
      stop(state);
      state.listeners.forEach(remove => remove());
      states.delete(video);
      if (warmedVideo === video) warmedVideo = null;
    });
  }

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      interruptBuffer();
      states.forEach(state => pause(state.video));
    }
    else {
      const group = document.querySelector('[data-edp-media-group]');
      if (group) warmHero(group);
      scheduleBuffer();
    }
  });

  window.EdiBotVideo = { init, warmHero, warm, play, pause, destroy };
})();
